// Clinical grading DTOs. One current grade per case; grade is a 4-tier pricing dimension that
// (with the service) resolves a price. draft → approved(locked): on chief approval the resolved
// price is snapshotted and becomes immutable — a later price-book edit never moves it.
import { z } from 'zod';

export const CASE_GRADES = ['a', 'b', 'c', 'd'] as const; // pricing tiers, high to low
export type CaseGrade = (typeof CASE_GRADES)[number];
export const CASE_GRADE_LABELS: Record<CaseGrade, string> = {
  a: 'Grade A (Premium)',
  b: 'Grade B (Standard)',
  c: 'Grade C (Economy)',
  d: 'Grade D (Basic)',
};

export const CASE_CLASSIFICATIONS = [
  'anterior',
  'posterior',
  'full_arch',
  'implant_supported',
  'ortho',
  'other',
] as const;
export type CaseClassification = (typeof CASE_CLASSIFICATIONS)[number];
export const CASE_CLASSIFICATION_LABELS: Record<CaseClassification, string> = {
  anterior: 'Anterior',
  posterior: 'Posterior',
  full_arch: 'Full arch',
  implant_supported: 'Implant-supported',
  ortho: 'Orthodontic',
  other: 'Other',
};

export const GRADE_STATUSES = ['draft', 'approved'] as const;
export type GradeStatus = (typeof GRADE_STATUSES)[number];

export interface CaseGradeDto {
  id: string;
  caseId: string;
  grade: CaseGrade;
  classification: CaseClassification | null;
  findings: string | null;
  shade: string | null;
  status: GradeStatus;
  gradedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  lockedUnitPrice: number | null;
  lockedTaxRateBps: number | null;
  currency: string;
  remakeChargeBps: number | null;
  createdAt: string;
  updatedAt: string;
}

export const upsertGradeSchema = z.object({
  grade: z.enum(CASE_GRADES),
  classification: z.enum(CASE_CLASSIFICATIONS).nullish(),
  findings: z.string().trim().max(4000).nullish(),
  shade: z.string().trim().max(40).nullish(),
  remakeChargeBps: z.number().int().min(0).max(10000).nullish(),
});
export type UpsertGradeRequest = z.infer<typeof upsertGradeSchema>;

export const approveGradeSchema = z.object({ note: z.string().trim().max(500).nullish() });
export type ApproveGradeRequest = z.infer<typeof approveGradeSchema>;
